import React, { useCallback, useEffect, useRef, useState } from "react";
import { authFetch } from "../utils/auth";

/**
 * /langmem — what conversation memory actually holds, per session.
 *
 * Reads the two server endpoints (`/api/memory/sessions[...]`) rather than any
 * local file: the live store is Redis, whose credentials only the server has.
 * The tier that answered each read is shown, so "this box has it, the cluster
 * does not" is visible instead of confusing.
 *
 * Read-only by design — nothing here can edit or clear a session's memory.
 */

const MODE_COLORS = { fast: "#2563eb", guided: "#7c3aed" };

const fmtTime = (t) => (t ? new Date(t * 1000).toLocaleString() : "—");

const Field = ({ label, children }) => (
  <div style={{ display: "flex", gap: 10, marginBottom: 4 }}>
    <span style={{ color: "#64748b", minWidth: 104, flexShrink: 0, fontSize: 12 }}>{label}</span>
    <span style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{children}</span>
  </div>
);

const Segment = ({ seg, index, active }) => {
  const list = (key) =>
    (seg[key] || []).map((v, i) => (
      <Field key={`${key}-${i}`} label={i === 0 ? key : ""}>{v}</Field>
    ));
  return (
    <div
      style={{
        border: "1px solid " + (active ? "#0ea5b7" : "#e2e8f0"),
        borderRadius: 8, padding: "12px 14px", marginBottom: 12,
        background: active ? "#f0fdff" : "#fff",
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
        [{index}] {seg.topic}
        {active && <span style={{ color: "#0ea5b7", marginLeft: 8, fontSize: 11 }}>ACTIVE</span>}
      </div>
      <Field label="summary">{seg.summary || "—"}</Field>
      <Field label="symptom">{seg.symptom || "—"}</Field>
      {list("environment")}
      {list("attempted")}
      {list("ruled_out")}
      {list("decisions")}
    </div>
  );
};

const Turn = ({ turn, n }) => {
  const color = MODE_COLORS[turn.m] || "#94a3b8";
  const chips = [
    ...(turn.ids || []).map((v) => `id ${v}`),
    ...(turn.menu || []).map((v) => v),
  ];
  return (
    <div style={{ borderLeft: `3px solid ${color}`, paddingLeft: 12, marginBottom: 18 }}>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6 }}>
        <span style={{ color, fontWeight: 600, textTransform: "uppercase" }}>{turn.m || "?"}</span>
        {"  ·  turn "}{n}{"  ·  "}{fmtTime(turn.t)}
      </div>
      <div style={{ fontSize: 13, whiteSpace: "pre-wrap", color: "#b45309", marginBottom: 6 }}>
        {turn.u}
      </div>
      <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{turn.a}</div>
      {chips.length > 0 && (
        <div style={{ marginTop: 6 }}>
          {chips.map((c, i) => (
            <span
              key={i}
              style={{
                display: "inline-block", background: "#f1f5f9", borderRadius: 4,
                padding: "2px 7px", marginRight: 6, marginTop: 4, fontSize: 11, color: "#475569",
              }}
            >
              {c}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const PAGE_SIZE = 10;

const pageBtn = (disabled) => ({
  fontSize: 11,
  padding: "2px 8px",
  borderRadius: 4,
  border: "1px solid #cbd5e1",
  background: "#fff",
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.45 : 1,
});

const LangMemViewer = () => {
  const [index, setIndex] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const selectedRef = useRef(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");

  const loadIndex = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const res = await authFetch(`/api/memory/sessions?limit=${PAGE_SIZE}&offset=${offset}`);
      if (!res.ok) {
        // 401 here is the ordinary "token expired in this tab" case, and saying
        // so beats an indefinite "loading…" — which is what this showed before.
        throw new Error(
          res.status === 401
            ? "401 — session expired, log in again"
            : `${res.status} ${res.statusText}`,
        );
      }
      const data = await res.json();
      setIndex(data);
      // Land on something real. An empty right-hand pane asking you to "pick a
      // session" is a wasted first screen when the answer is almost always the
      // newest one.
      const first = (data.sessions || [])[0];
      if (first && !selectedRef.current) open(first.session_id);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [offset]);

  useEffect(() => { loadIndex(); }, [loadIndex]);

  // /langmem?session=<id> opens that session directly — the copilot logs a
  // session_id on every turn, so pasting one in beats hunting the list.
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get("session");
    if (wanted) open(wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = useCallback(async (sid) => {
    selectedRef.current = sid;
    setSelected(sid);
    setDetail(null);
    try {
      const res = await authFetch(`/api/memory/sessions/${encodeURIComponent(sid)}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      setDetail(await res.json());
    } catch (e) {
      // Without this the pane sits on "loading…" indefinitely, which is what a
      // 404 for a session that has since expired looked like.
      setDetail({ error: String(e.message || e) });
      setError(String(e.message || e));
    }
  }, []);

  const sessions = (index?.sessions || []).filter((s) => {
    if (!filter.trim()) return true;
    const hay = `${s.session_id} ${(s.topics || []).join(" ")} ${s.product || ""}`.toLowerCase();
    return hay.includes(filter.trim().toLowerCase());
  });

  const state = detail?.state || {};

  return (
    // Fixed to the viewport on purpose: the app's route wrapper is centred
    // (.App { text-align: center }) and constrained, which left this page as a
    // narrow column floating in the middle of the screen. A tool page wants the
    // whole window.
    <div
      style={{
        position: "fixed", inset: 0, display: "flex", background: "#fff",
        fontFamily: "Inter, system-ui, sans-serif", textAlign: "left", zIndex: 1,
      }}
    >
      <div style={{ width: 360, borderRight: "1px solid #e2e8f0", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: 14, borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Conversation memory</div>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="filter this page by id, topic, product"
            style={{ width: "100%", padding: "6px 9px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13 }}
          />
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 8 }}>
            {loading
              ? "loading…"
              : index
                ? `${offset + 1}–${Math.min(offset + PAGE_SIZE, index.count)} of ${index.count} · redis ${index.tiers.redis_connected ? "connected" : "DOWN"} (${index.tiers.redis}) · file (${index.tiers.file})`
                : "no data"}
            <button
              onClick={loadIndex}
              style={{ marginLeft: 8, fontSize: 11, cursor: "pointer", border: "1px solid #cbd5e1", borderRadius: 4, background: "#fff", padding: "1px 6px" }}
            >
              refresh
            </button>
          </div>
          {error && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 6 }}>{error}</div>}
          <div style={{ display: "flex", gap: 6, marginTop: 8, alignItems: "center" }}>
            <button
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0 || loading}
              style={pageBtn(offset === 0 || loading)}
            >
              ← prev
            </button>
            <button
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={!index?.has_more || loading}
              style={pageBtn(!index?.has_more || loading)}
            >
              next →
            </button>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>
              page {Math.floor(offset / PAGE_SIZE) + 1}
            </span>
          </div>
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          {sessions.map((s) => (
            <div
              key={s.session_id}
              onClick={() => open(s.session_id)}
              style={{
                padding: "10px 14px", borderBottom: "1px solid #f1f5f9", cursor: "pointer",
                background: s.session_id === selected ? "#f0fdff" : "#fff",
                borderLeft: s.session_id === selected ? "3px solid #0ea5b7" : "3px solid transparent",
              }}
            >
              <div style={{ fontSize: 11, fontFamily: "ui-monospace, monospace", wordBreak: "break-all" }}>
                {s.session_id}
              </div>
              <div style={{ fontSize: 12, color: "#0f766e", marginTop: 3 }}>
                {(s.topics || []).join(" · ") || "no investigation yet"}
              </div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 3 }}>
                {s.turns} turns · {(s.modes || []).join("+") || "—"} · {s.source} · {fmtTime(s.last_activity)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "20px 26px", background: "#f8fafc" }}>
        {!selected && <div style={{ color: "#64748b", marginTop: 60, textAlign: "center" }}>Pick a session.</div>}
        {selected && !detail && <div style={{ color: "#64748b" }}>loading…</div>}
        {detail?.error && (
          <div style={{ color: "#dc2626", fontSize: 13 }}>{detail.error}</div>
        )}
        {detail && !detail.error && (
          <>
            <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 14px", marginBottom: 16, background: "#fff" }}>
              <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 12, marginBottom: 8 }}>{detail.session_id}</div>
              <Field label="source">
                {detail.source}
                {detail.ttl_seconds ? ` · ttl ${Math.round(detail.ttl_seconds / 3600)}h` : ""}
              </Field>
              <Field label="product">{state.product || "—"}</Field>
              <Field label="turns">{(state.turns || []).length}</Field>
            </div>

            <div style={{ fontSize: 12, color: "#64748b", margin: "0 0 8px", letterSpacing: ".05em" }}>
              INVESTIGATIONS (extracted by LangMem)
            </div>
            {(state.contexts || []).length === 0 && (
              <div style={{ color: "#64748b", fontSize: 13, marginBottom: 16 }}>none extracted yet</div>
            )}
            {(state.contexts || []).map((seg, i) => (
              <Segment key={seg._id || i} seg={seg} index={i} active={i === state.active} />
            ))}

            <div style={{ fontSize: 12, color: "#64748b", margin: "18px 0 10px", letterSpacing: ".05em" }}>
              RAW TURNS (verbatim, never trimmed)
            </div>
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px" }}>
              {(state.turns || []).map((t, i) => <Turn key={i} turn={t} n={i + 1} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LangMemViewer;
